import React, { useEffect, useState } from "react";
import styles from "./TimeSelectionView.module.css";
import {
    TimeSelectionRow,
    ProposedDate,
    DayPartSelection,
    RecommendationStatus,
} from "./types";
import { DayPart } from "../../gql/types";
import TimeRangeRowView from "./TimeRangeRowView";
import DayPartIconButton from "./DayPartIconButton";
import { formatDateToLabel } from "./TimeFormatter";

interface TimeSelectionSelectionViewProps {
    mode: "time" | "daypart";
    /** For time mode: a list of time rows (read-only options) */
    initialTimes?: TimeSelectionRow[] | DayPartSelection[];
    /**
     * For daypart mode: a list of ProposedDate objects,
     * each with a single dayPart. 
     */
    proposedDates?: ProposedDate[];
    /** If true, group daypart entries by date in the UI. Default false. */
    groupByDate?: boolean;
    /**
     * Callback that returns the user's final selection:
     * - In time mode: an array of TimeSelectionRow (the rows the user clicked).
     * - In daypart mode: an array of DayPartSelection (each date + single daypart).
     */
    onSelectionChange?: (selection: TimeSelectionRow[] | DayPartSelection[]) => void;
    singleSelection?: boolean;
    readOnly?: boolean;
    filterPastDates?: boolean;
}

function isTimeSelectionRowArray(arr: any[]): arr is TimeSelectionRow[] {
    return arr.length > 0 && "startTime" in arr[0] && "endTime" in arr[0];
}

const TimeSelectionSelectionView: React.FC<TimeSelectionSelectionViewProps> = ({
    mode,
    initialTimes,
    proposedDates,
    groupByDate = false,
    onSelectionChange,
    singleSelection,
    readOnly = false,
    filterPastDates = true,
}) => {
    /* ============ TIME MODE ============ */
    const [selectedTimeRows, setSelectedTimeRows] = useState<TimeSelectionRow[]>(initialTimes && isTimeSelectionRowArray(initialTimes) ? initialTimes : []);

    const handleTimeRowClick = (row: TimeSelectionRow) => {
        const exists = selectedTimeRows.some((r) => r.id === row.id);
        const newSelection = exists
            ? selectedTimeRows.filter((r) => r.id !== row.id)
            : [...selectedTimeRows, row];
        setSelectedTimeRows(newSelection);
        onSelectionChange?.(newSelection);
    };

    /* ============ DAYPART MODE ============ */
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const filteredProposedDates = proposedDates?.filter((pd) => {
        if (!filterPastDates) return true;
        function parseToLocalMidnight(dateStr: string): Date {
            const [y, m, d] = dateStr.split("T")[0].split("-").map(Number);
            return new Date(y, m - 1, d);
        }
        const rowDate = parseToLocalMidnight(pd.dateString);
        return rowDate >= today;
    });

    const [selectedDayParts, setSelectedDayParts] = useState<DayPartSelection[]>(initialTimes && !isTimeSelectionRowArray(initialTimes) ? initialTimes : []);

    useEffect(() => {
        if (initialTimes && !isTimeSelectionRowArray(initialTimes)) {
            setSelectedDayParts(initialTimes);
        }
    }, [initialTimes]);

    const isDayPartSelected = (dateLabel: string, dp: DayPart) => {
        return selectedDayParts.some(
            (sel) => sel.selectedDate === dateLabel && sel.selectedDayPart === dp
        );
    };

    const handleDayPartClick = (dateLabel: string, dp: DayPart) => {
        if (singleSelection) {
            const isAlreadySelected = selectedDayParts.length === 1 &&
                selectedDayParts[0].selectedDate === dateLabel &&
                selectedDayParts[0].selectedDayPart === dp;

            if (isAlreadySelected) {
                setSelectedDayParts([]);
                onSelectionChange?.([]);
                return;
            }

            if (selectedDayParts.length > 0) {
                // Something else is selected; do nothing
                return;
            }

            const newSelection = [{ selectedDate: dateLabel, selectedDayPart: dp }];
            setSelectedDayParts(newSelection);
            onSelectionChange?.(newSelection);
            return;
        }

        const index = selectedDayParts.findIndex(
            (sel) => sel.selectedDate === dateLabel && sel.selectedDayPart === dp
        );
        const newSelection =
            index >= 0
                ? [
                    ...selectedDayParts.slice(0, index),
                    ...selectedDayParts.slice(index + 1),
                ]
                : [...selectedDayParts, { selectedDate: dateLabel, selectedDayPart: dp }];
        setSelectedDayParts(newSelection);

        const filteredSelection = newSelection.filter(sel =>
            filteredProposedDates?.some(pd =>
                pd.dateString === sel.selectedDate && pd.dayPart === sel.selectedDayPart
            )
        );
        onSelectionChange?.(filteredSelection);
    };
    // Helper to group ProposedDate items by date if groupByDate = true
    const groupDates = (items: ProposedDate[]) => {
        return items.reduce<Record<string, ProposedDate[]>>((acc, pd) => {
            if (!acc[pd.dateString]) {
                acc[pd.dateString] = [];
            }
            acc[pd.dateString].push(pd);
            return acc;
        }, {});
    };

    /* ============ RENDER ============ */
    if (mode === "time") {
        // TIME MODE
        return (
            <div className={styles.timeSelectionTable}>
                {initialTimes && isTimeSelectionRowArray(initialTimes) && initialTimes?.map((row) => {
                    const isSelected = selectedTimeRows.some((r) => r.id === row.id);
                    return (
                        <div
                            key={row.id}
                            className={`${styles.row} ${isSelected ? styles.selectedRow : ""}`}
                            onClick={() => handleTimeRowClick(row)}
                        >
                            <TimeRangeRowView startTime={row.startTime} endTime={row.endTime} />
                        </div>
                    );
                })}
            </div>
        );
    }

    // "dayPart mode"
    if (!filteredProposedDates) return null;

    if (groupByDate) {
        // Group them by date, so we show each date once, with multiple dayparts under it
        const grouped = groupDates(filteredProposedDates);
        return (
            <div className={`${styles.daySelectionTable} ${styles.displayGap}`}>
                {Object.entries(grouped).map(([dateString, items]) => {
                    const anySelected = items.some((pd) =>
                        isDayPartSelected(pd.dateString, pd.dayPart)
                    );
                    return (
                        <div
                            key={dateString}
                            className={`${styles.dateRow} ${readOnly ? styles.readOnly : ""} ${singleSelection ? (anySelected ? styles.selectedRow : (selectedDayParts.length > 0 ? styles.readOnly : "")) : ""}`}
                        >
                            <div
                                className={`${styles.dateLabel} ${singleSelection && anySelected ? styles.selectedDateLabel : ""}`}>
                                {formatDateToLabel(dateString)}
                            </div>
                            <div className={styles.dayPartRow}>
                                {items.map((pd, idx) => {
                                    const selected = isDayPartSelected(pd.dateString, pd.dayPart);
                                    return (
                                        <DayPartIconButton
                                            key={`${pd.dateString}_${idx}`}
                                            selected={selected}
                                            useTransparentStyling={singleSelection && !selected && anySelected}
                                            onClick={() => handleDayPartClick(pd.dateString, pd.dayPart)}
                                            dayPart={pd.dayPart}
                                            readOnly={readOnly || (singleSelection && !selected && selectedDayParts.length > 0)}
                                            bannerText={pd.bannerText}
                                            recommendation={pd.recommendation ?? RecommendationStatus.Neutral}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    } else {
        // No grouping: each ProposedDate is its own row
        return (
            <div className={`${styles.daySelectionTable} ${styles.displayGap}`}>
                {filteredProposedDates.map((pd, idx) => {
                    const selected = isDayPartSelected(pd.dateString, pd.dayPart);
                    return (
                        <div
                            key={pd.dateString}
                            className={`${styles.dateRow} ${readOnly ? styles.readOnly : ""} ${singleSelection ? (selected ? styles.selectedRow : (selectedDayParts.length > 0 ? styles.readOnly : "")) : ""}`}
                        >
                            <div
                                className={`${styles.dateLabel} ${singleSelection && selected ? styles.selectedDateLabel : ""}`}>
                                {formatDateToLabel(pd.dateString)}
                            </div>
                            <DayPartIconButton
                                key={`${pd.dateString}_${idx}`}
                                selected={selected}
                                onClick={() => handleDayPartClick(pd.dateString, pd.dayPart)}
                                readOnly={readOnly || (singleSelection && !selected && selectedDayParts.length > 0)}
                                dayPart={pd.dayPart}
                                bannerText={pd.bannerText} // optional
                                recommendation={pd.recommendation ?? RecommendationStatus.Neutral}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
};

export default TimeSelectionSelectionView;
